import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getFetcher } from "../../../api/fetchers.ts";
import { endpoints } from "../../../api/endpoints.ts";
import { useUser } from "../../context/UserContext";
import Spinner from "../spinner/Spinner.tsx";
import { calculateDiscountedPrice } from "../../utils/functions.tsx";
import logo from "../../assets/images/novaLogo.png";
import Wenilogo from "../../assets/images/WeniLogo.png";
import { useLanguage } from "../../context/LanguageContext.tsx";
import { useCurrency } from "../../context/CurrencyContext";

interface Order {
  id: string;
  orderNumber: string;
  userId: string;
  basketId: string;
  supplierId: string;
  dealerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  city: string;
  district: string;
  couponCode: string | null;
  deliveryStatus: number;
  deliveryAddress: string;
  deliveryNumber: string;
  deliveryDate: string;
  status: boolean;
  updatedAt: string;
  createdAt: string;
  totalAmount: string;
  productInfo: {
    productId: string;
    images: string;
    productName: string;
    productPrice: number;
    quantity: number;
  };
  supplierInfo: {
    supplierId: string;
    supplierFullName: string;
    supplierMarketPlaceName: string;
  };
}

interface B2B {
  id: string;
  name: string;
  ratio: number;
}

interface OrderGroup {
  date: string;
  orders: Order[];
}

const Orders: React.FC = () => {
  const { t } = useLanguage();
  const { exchangeRate, currencySymbol } = useCurrency();
  const printRef = useRef<HTMLDivElement | null>(null);
  const [orders, setOrders] = useState<Order[][]>([]);
  const { user, isLoading: userLoading } = useUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [showProformaModal, setShowProformaModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>();
  const [userBusinessPackage, setUserBusinessPackage] = useState<B2B>();
  const [showPrintBtn, setShowPrintBtn] = useState(true);

  const fetchBusinessPackageData = async () => {
    if (user) {
      try {
        const businessPackage = await getFetcher(
          endpoints.user.getBusinessPackageById.replace(
            "{id}",
            user.businessPackageId
          )
        );
        if (businessPackage.result) {
          setUserBusinessPackage(businessPackage.businessPackage);
        }
      } catch (error) {
        console.error("Error fetching favorites:", error);
      }
    }
  };

  useEffect(() => {
    fetchBusinessPackageData();
  }, []);

  // FETCH ORDERS
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        console.error("User not found");
        setLoading(false);
        return;
      }

      try {
        const response = await getFetcher(endpoints.user.getUserOrders);
        if (response.result) {
          setOrders(response.orders);
        } else {
          console.error("Error fetching orders:", response.message);
        }
      } catch (error: any) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading) {
      fetchOrders();
    }
  }, [user, userLoading]);

  const orderGroups: OrderGroup[] = orders
    ?.map((group) => {
      return {
        date: new Date(group[0].deliveryDate).toLocaleDateString(),
        orders: group,
        orderId: group[0].orderNumber,
        totalAmount: group[0]?.totalAmount,
      };
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (loading) {
    return (
      <div className="spinner-container">
        <Spinner />
      </div>
    );
  }

  // CALCULATE ITEM PRICE
  const calculateItemTotalPrice = (item: any): any => {
    const discountedPrice = calculateDiscountedPrice(
      item?.productInfo?.price,
      item?.productInfo?.discount,
      userBusinessPackage ? userBusinessPackage?.ratio : 0,
      exchangeRate
    );
    let totalPrice = discountedPrice * item?.basket?.quantity;

    if (item?.basket?.bulkDiscount) {
      const bulkDiscountedPrice = parseFloat(calculateBulkDiscount(item));
      totalPrice = bulkDiscountedPrice;
    }

    if (item?.campaignInfo) {
      if (
        item.campaignInfo.dailyCampaign &&
        item.campaignInfo.dailyCampaign.dailyCampaignTypeId == 3
      ) {
        totalPrice -=
          item.campaignInfo.dailyCampaign.dailyValue * item.basket.quantity;
      }

      if (
        item.campaignInfo.companyCampaign &&
        item.campaignInfo.companyCampaign.companyCampaignTypeId == 3
      ) {
        totalPrice -=
          item.campaignInfo.companyCampaign.companyValue * item.basket.quantity;
      }
    }

    return totalPrice;
  };

  const calculateBulkDiscount = (item: any) => {
    const bulkDiscounts = item.productInfo.bulkDiscounts
      ? item.productInfo.bulkDiscounts
      : [];

    const discountPrice = calculateDiscountedPrice(
      item.productInfo.price,
      item.productInfo.discount,
      userBusinessPackage ? userBusinessPackage?.ratio : 0,
      exchangeRate
    );

    if (item.basket.bulkDiscount) {
      const applicableBulkDiscount = bulkDiscounts
        .filter((bulk: any) => item?.basket?.quantity >= bulk?.quantity)
        .sort((a: any, b: any) => b?.quantity - a?.quantity)[0];

      if (applicableBulkDiscount) {
        const totalPrice = discountPrice * item?.basket?.quantity;
        const totalDiscountAmount =
          (totalPrice * applicableBulkDiscount.discount) / 100;
        const bulkPrice = (totalPrice - totalDiscountAmount).toFixed(2);

        return bulkPrice;
      } else {
        return (discountPrice * item?.basket?.quantity).toFixed(2);
      }
    } else {
      return (discountPrice * item?.basket?.quantity).toFixed(2);
    }
  };

  // TOTAL PRICE
  // const calculateTotalPrice = (item: any) => {
  //   let totalPrice: number =
  //     item?.reduce(
  //       (sum: any, item: any) => sum + calculateItemTotalPrice(item),
  //       0
  //     ) || 0;
  //   return totalPrice;
  // };

  const orderStatusFunc = (status: number) => {
    switch (status) {
      case 1:
        return t("orders", "status1");
      case 2:
        return t("orders", "status2");
      case 3:
        return t("orders", "status3");
      case 4:
        return t("orders", "status4");
      case 5:
        return t("orders", "status5");
      case 6:
        return t("orders", "status6");
      default:
        return t("orders", "status7");
    }
  };

  const handleProforma = (group: any): any => {
    setSelectedOrder(group);
    setShowProformaModal(true);
  };

  const handlePrint = () => {
    setShowPrintBtn(false);
    setTimeout(() => {
      const printContents = printRef?.current?.innerHTML;
      const originalContents = document.body.innerHTML;

      printContents ? (document.body.innerHTML = printContents) : null;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.href = "/";
      window.location.reload();
    }, 1000);
    setTimeout(() => {
      setShowPrintBtn(true);
    }, 3000);
  };

  function extractProductInfo(orders: any) {
    return orders?.map((order: any) => order.productInfo);
  }

  const productInfos = extractProductInfo(selectedOrder && selectedOrder);

  const totalPrice = productInfos?.reduce((total: number, product: any) => {
    const productTotal =
      (product?.productPrice || 0) * (product?.quantity || 0);
    return total + productTotal * exchangeRate;
  }, 0);

  const discountPercentage =
    Array.isArray(selectedOrder) &&
      selectedOrder.length > 0 &&
      selectedOrder[0]?.totalAmount
      ? ((totalPrice - selectedOrder[0]?.totalAmount) / totalPrice) * 100
      : 0;

  return (
    <>
      <div className="p-4 md:px-6 md:py-4  rounded-lg h-full overflow-y-auto">
        <div className="p-2">
          <h5 className="text-primary-color text-3xl font-semibold">
            {t("orders", "title")}
          </h5>
        </div>
        {orderGroups.length < 1 ? (
          <div className=" flex justify-center items-center w-full h-full">
            <p className=" font-bold text-sm">{t("orders", "noData")}</p>
          </div>
        ) : (
          <div className=" border border-[#DCDCDC]  rounded-t-lg ">
            <div className="rounded-t-lg bg-primary-color text-white py-4 px-4 sm:px-8">
              <div className=" flex justify-start xl:justify-between items-start xl:items-center flex-col xl:flex-row ">
                {/* Kullanıcı Adı */}
                <div className="flex flex-row items-center gap-2 ">
                  <h2 className="font-poppins font-semibold text-[16px] md:text-[18px] xl:text-[24px]">
                    {t("orders", "name")}:
                  </h2>
                  <div className="flex gap-x-1 xl:mt-[4px] text-[11px] md:text-[13px] xl:text-[16px] text-[#E7E7E7] font-poppins font-semibold  md:-mt-0">
                    <p>{user?.firstName}</p>
                    <p>{user?.lastName}</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex flex-row items-center gap-2">
                  <h2 className="font-poppins font-semibold text-[16px] md:text-[18px] xl:text-[24px]">
                    {t("orders", "email")}:
                  </h2>
                  <div className="text-[11px] md:text-[13px] xl:text-[16px] text-[#E7E7E7] font-poppins font-semibold mt-0 xl:mt-[4px]">
                    <p>{user?.email}</p>
                  </div>
                </div>

                {/* Telefon */}
                <div className="flex flex-row items-center gap-2">
                  <h2 className="font-poppins font-semibold text-[16px] md:text-[18px] xl:text-[24px]">
                    {t("orders", "phone")}:
                  </h2>
                  <div className="text-[11px] md:text-[13px] xl:text-[16px] text-[#E7E7E7] font-poppins font-semibold mt-0 xl:mt-[4px]">
                    <p>{user?.phoneNumber}</p>
                  </div>
                </div>
              </div>
            </div>

            {orderGroups.map((group: any, index) => {
              return (
                <div key={index} className="mb-8  p-4 flex flex-col">
                  <div className=" px-4 text-[#353535] flex items-center gap-x-2 font-poppins font-semibold">
                    <p className=" text-[15px]">
                      {t("orders", "orderNumber")}:{" "}
                    </p>
                    <p className=" text-[14px]">#{group?.orderId}</p>
                  </div>
                  <div className="flex flex-col lg:flex-row justify-between items-center shadow-xl rounded-[20px] p-4 gap-4">
                    {/* Siparişler */}
                    <div className="flex overflow-x-auto gap-4 flex-1 w-full py-2">
                      {group?.orders.map((order: any, idx: any) => {
                        return (
                          <div
                            key={idx}
                            className="flex-shrink-0 w-48 rounded-lg shadow-md bg-white overflow-hidden"
                          >
                            <div className="p-2">
                              <p className="truncate w-full text-center font-rubik font-[500] text-primary-color text-[13px]">
                                {order?.productInfo?.productName}
                              </p>
                            </div>
                            <div className="h-32 overflow-hidden">
                              <img
                                src={order?.productInfo?.images[0]}
                                alt="Product"
                                className="w-full h-full object-contain transition-transform duration-300 hover:scale-105 pt-2"
                              />
                            </div>
                            <div className="flex flex-col justify-center w-full px-2">
                              <div className="flex justify-between items-center mt-1">
                                <p className="text-xs text-gray-600">
                                  {order?.supplierInfo?.supplierMarketPlaceName}
                                </p>
                                <span className="block font-semibold text-sm font-poppins text-primary-color w-full text-center">
                                  {currencySymbol}
                                  {order?.basket?.bulkDiscount
                                    ? calculateBulkDiscount(order)
                                    : calculateItemTotalPrice(order)?.toFixed(2)}
                                </span>
                              </div>
                              <p className="text-[10px] text-green-800 my-1 font-bold text-start">
                                {orderStatusFunc(order?.deliveryStatus)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Detay ve Toplam */}
                    <div className="flex-1 flex flex-col justify-center items-start gap-5 md:items-center xl:flex-row">
                      <div className="w-full md:w-auto">
                        <div className="flex items-start lg:items-center flex-col lg:flex-row gap-x-2">
                          <h2 className="font-rubik text-[#141C3A] text-[20px] lg:text-[28px] font-[500]">
                            {t("orders", "date")}:
                          </h2>
                          <p className="text-[#5F6C72] mt-1 font-poppins font-normal text-[16px] md:text-[19px] min-w-full lg:min-w-[200px]">
                            {group?.date}
                          </p>
                        </div>
                        <div className="flex items-start lg:items-center flex-col lg:flex-row gap-x-2 mt-2">
                          <h2 className="font-rubik text-[#141C3A] text-[20px] lg:text-[28px] font-[500]">
                            {t("orders", "total")}:
                          </h2>
                          <div className="flex gap-x-1">
                            <p className="text-[#5F6C72] mt-1 font-poppins font-normal text-[16px] lg:text-[19px]">
                              {currencySymbol}
                              {group?.totalAmount}
                            </p>
                            <p className="text-[#5F6C72] mt-1 font-poppins font-normal text-[16px] lg:text-[19px]">
                              ({group?.orders?.length}{" "}
                              {t("orders", "productQuantity")})
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-y-3 w-full md:w-auto">
                        <Link
                          to="/profile/order-detail"
                          state={{
                            groupOrders: group.orders,
                            date: group.date,
                            totalPrice: group?.totalAmount,
                            orderCount: group.orders.length,
                            orderId: group.orderId,
                          }}
                          className="bg-yellow-color text-[#1AAB00] font-poppins font-normal xl:font-extrabold px-6 py-1 rounded-lg active:scale-90 transition-transform text-center min-w-[150px] lg:min-w-[240px]"
                        >
                          {t("orders", "orderButton")}
                        </Link>
                        {user?.businessPackageId !== "10" && (
                          <button
                            onClick={() => handleProforma(group?.orders)}
                            className="bg-primary-color text-yellow-color font-poppins font-normal xl:font-extrabold px-6 py-1 rounded-lg active:scale-90 transition-transform text-center min-w-[150px] lg:min-w-[240px]"
                          >
                            {t("orders", "proformaButton")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* proformo */}
        {showProformaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto">
            <div
              ref={printRef}
              className="relative p-6 rounded-xl bg-gray-50 w-full max-w-6xl overflow-y-auto max-h-[80vh] border border-gray-300 shadow-xl"
            >
              {/* Close Button */}
              {showPrintBtn && (
                <button
                  onClick={() => setShowProformaModal(false)}
                  className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 focus:outline-none"
                >
                  Close
                </button>
              )}

              {/* Header Section */}
              <div className="flex justify-center items-center mb-6">
                <h5 className="text-xl font-bold text-gray-800">
                  {t("invoice", "title")}
                </h5>
              </div>

              {/* Logos */}
              <div className="flex justify-between items-center mb-8 gap-4">
                <img
                  src={Wenilogo}
                  alt="logo"
                  className="w-2/3 max-w-[210px]"
                />
                <img src={logo} alt="logo" className="w-2/3 max-w-[210px]" />
              </div>

              {/* Supplier Info */}
              <div className=" flex justify-between items-center gap-6 border-b pb-6 mb-6">
                <div className="">
                  <h2 className=" font-bold">SELLER</h2>
                  <p className="text-sm text-gray-800">
                    {selectedOrder[0]?.supplierInfo?.supplierMarketPlaceName}
                  </p>
                  <p className="text-sm text-gray-800">
                    {selectedOrder[0]?.supplierInfo?.supplierEmail}
                  </p>
                  <p className="text-sm text-gray-800">
                    Tel: {selectedOrder[0]?.supplierInfo?.supplierPhone}
                  </p>
                  <p className="text-sm text-gray-800">
                    {selectedOrder[0]?.supplierInfo?.supplierAdress}
                  </p>
                </div>

                <div className="flex justify-start ">
                  <div>
                    <h2 className=" font-bold">BUYER</h2>
                    <p className="text-sm text-gray-800">{user?.companyName}</p>
                    <p className="text-sm text-gray-800">
                      {user?.firstName + " " + user?.lastName}
                    </p>
                    <p className="text-sm text-gray-800">{user?.email}</p>
                    <p className="text-sm text-gray-800">{user?.taxNumber}</p>
                    <p className="text-sm text-gray-800">
                      {user?.openAddress.split(",")[1]}
                    </p>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <div className="">
                <div className="grid grid-cols-5 gap-2 text-sm font-bold text-gray-700 border-b py-2">
                  <p className="">{t("invoice", "code")}</p>
                  <p className="">{t("invoice", "name")}</p>
                  <p className="text-center">{t("invoice", "quantity")}</p>
                  <p className="text-right">{t("invoice", "unitPrice")}</p>
                  <p className="text-right">{t("invoice", "totalPrice")}</p>
                </div>
                {productInfos.map((product: any) => (
                  <div
                    key={product?.code}
                    className="grid grid-cols-5 gap-0 text-xs text-gray-800 border-b py-[1.5px]"
                  >
                    <p className=" truncate">{product?.code}</p>
                    <p className=" truncate">{product?.productName}</p>
                    <p className=" text-center">{product?.quantity}</p>
                    <p className=" text-right">
                      {currencySymbol}
                      {calculateDiscountedPrice(
                        product?.productPrice,
                        product?.discount,
                        userBusinessPackage ? userBusinessPackage?.ratio : 0,
                        exchangeRate
                      ).toFixed(2)}
                    </p>
                    <p className=" text-right">
                      {currencySymbol}
                      {(
                        calculateDiscountedPrice(
                          product?.productPrice,
                          product?.discount,
                          userBusinessPackage ? userBusinessPackage?.ratio : 0,
                          exchangeRate
                        ) * product?.quantity
                      ).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex flex-row justify-between items-start gap-4 mt-32">
                {/* Sol Panel */}
                <div className="flex flex-col gap-1 p-2 rounded-2xl text-gray-800 w-full max-w-md">
                  <div className="flex justify-between w-full gap-x-2 border-b pb-1">
                    <p className="font-semibold text-[10px] text-gray-600 min-w-[120px]">
                      Delivery Term:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900 text-right min-w-[120px]">
                      EXW
                    </p>
                  </div>

                  <div className="flex justify-between w-full gap-x-2 border-b pb-1">
                    <p className="font-semibold text-[10px] text-gray-600 min-w-[120px]">
                      Payment:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900 text-right min-w-[120px]">
                      100% Online Payment
                    </p>
                  </div>

                  <div className="flex justify-between w-full gap-x-2 border-b pb-1">
                    <p className="font-semibold text-[10px] text-gray-600 min-w-[120px]">
                      Delivery:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900 text-right min-w-[120px]">
                      Delivery 10 days after payment
                    </p>
                  </div>

                  <div className="flex justify-between w-full gap-x-2 pt-1">
                    <p className="font-semibold text-[10px] text-gray-600 min-w-[120px]">
                      Account:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900 text-right min-w-[120px]">
                      Wenimed GmbH
                    </p>
                  </div>
                </div>

                {/* Sağ Panel */}
                <div className="flex flex-col gap-1 p-2 rounded-2xl text-gray-800 w-full max-w-md">
                  <div className="flex justify-between w-full gap-x-2 border-b pb-1">
                    <p className="font-semibold text-[10px] text-gray-600">
                      {t("invoice", "totalPrice")}:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900">
                      {currencySymbol}
                      {totalPrice.toFixed(2)}
                    </p>
                  </div>

                  <div className="flex justify-between w-full gap-x-2 border-b pb-1">
                    <p className="font-semibold text-[10px] text-gray-600">
                      {t("invoice", "tax")}:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900">%0</p>
                  </div>

                  <div className="flex justify-between w-full gap-x-2 border-b pb-1">
                    <p className="font-semibold text-[10px] text-gray-600">
                      {t("invoice", "discount")} (%):
                    </p>
                    <p className="text-[10px] font-bold text-green-600">
                      {Math.abs(discountPercentage).toFixed(2)}%
                    </p>
                  </div>

                  <div className="flex justify-between w-full gap-x-2 pt-1">
                    <p className="font-semibold text-[10px] text-gray-600">
                      {t("invoice", "allTotal")}:
                    </p>
                    <p className="text-[10px] font-bold text-gray-900">
                      {currencySymbol}
                      {Number(selectedOrder[0]?.totalAmount).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-20 text-center text-gray-500 text-[10px] font-semibold">
                <p>{t("invoice", "create")}</p>
                <p className="mt-2">
                  © 2025 Novamedshop. {t("invoice", "reservered")}
                </p>
              </div>

              {/* Print Button */}
              {showPrintBtn && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handlePrint}
                    className="bg-primary-color text-white px-6 py-2 rounded-lg shadow hover:bg-opacity-80 transition-all"
                  >
                    {t("invoice", "printButton")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default Orders;
